(function () {
  'use strict';
  // A remembered estimate destination is valid only in its original Shopify market/currency.
  const deliveryCountryFromStorage = (storage, marketCountry, currency) => {
    try {
      const saved = JSON.parse(storage.getItem('groomskull_delivery_country'));
      return saved && /^[A-Z]{2}$/.test(saved.country || '') && marketCountry && currency &&
        saved.marketCountry === marketCountry && saved.currency === currency ? saved.country : '';
    } catch (_) { return ''; }
  };
  const saveDeliveryCountry = (storage, country, marketCountry, currency) => {
    if (!/^[A-Z]{2}$/.test(country || '') || !marketCountry || !currency) return;
    try { storage.setItem('groomskull_delivery_country', JSON.stringify({country, marketCountry, currency})); } catch (_) {}
  };

  const DAY = 86400000;
  function isoDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const date = new Date(value + 'T00:00:00.000Z');
    return Number.isFinite(date.valueOf()) && date.toISOString().slice(0, 10) === value ? date : null;
  }
  function calendarDate(now = new Date(), timeZone = 'Europe/Paris') {
    if (!Number.isFinite(now.valueOf())) return null;
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const part = name => parts.find(p => p.type === name).value;
    return `${part('year')}-${part('month')}-${part('day')}`;
  }
  function addCalendarDays(day, count) {
    const date = isoDate(day);
    if (!date || !Number.isInteger(count) || count < 0) return null;
    date.setUTCDate(date.getUTCDate() + count);
    return date.toISOString().slice(0, 10);
  }
  function windowForCountry(data, country) {
    if (data?.version !== 1 || data.metric !== 'order_to_delivery_calendar_days' || data.includesPreparation !== true || data.calendarTimeZone !== 'Europe/Paris') return null;
    const policy = data.policy;
    const sample = data.countries?.[String(country).toUpperCase()];
    if (!policy || !sample || !Number.isInteger(sample.totalSamples) || sample.totalSamples < 10 || !Array.isArray(sample.routes)) return null;
    if (!Number.isInteger(policy.minimumCountrySamples) || policy.minimumCountrySamples < 10 || !Number.isInteger(policy.minimumRouteSamples) || policy.minimumRouteSamples < 5 || !Number.isFinite(policy.minimumCoverage) || policy.minimumCoverage < 0.8 || policy.minimumCoverage > 1) return null;
    if (sample.totalSamples < policy.minimumCountrySamples) return null;
    if (sample.routes.some(r => !Number.isInteger(r.samples) || r.samples < 0 || !Number.isFinite(r.meanDays) || r.meanDays < 0 || !Number.isFinite(r.p85Days) || r.p85Days < 0)) return null;
    if (sample.routes.reduce((sum, r) => sum + r.samples, 0) !== sample.totalSamples) return null;
    const routes = sample.routes.filter(r => r.samples >= policy.minimumRouteSamples);
    const supported = routes.reduce((sum, r) => sum + r.samples, 0);
    const coverage = supported / sample.totalSamples;
    if (!supported || coverage < policy.minimumCoverage) return null;
    const minimumDays = Math.floor(routes.reduce((sum, r) => sum + r.samples * r.meanDays, 0) / supported);
    const maximumDays = Math.ceil(Math.max(...routes.map(r => r.p85Days)));
    if (maximumDays < minimumDays || maximumDays > 365) return null;
    return { minimumDays, maximumDays, coverage, supportedSamples: supported, totalSamples: sample.totalSamples };
  }
  function estimate({ data, country, now = new Date(), available = true, subscription = false }) {
    if (!available) return { status: 'unavailable' };
    const today = calendarDate(now);
    const verified = isoDate(data?.verifiedOn);
    if (!today || !verified || !Number.isInteger(data?.maxAgeDays) || data.maxAgeDays < 1 || data.maxAgeDays > 30) return { status: 'missing' };
    const age = (isoDate(today) - verified) / DAY;
    if (age < 0 || age >= data.maxAgeDays) return { status: 'stale' };
    const range = windowForCountry(data, country);
    if (!range) return { status: 'unsupported' };
    return { status: 'ready', ...range, start: addCalendarDays(today, range.minimumDays), end: addCalendarDays(today, range.maximumDays), label: 'Livraison entre le' };
  }
  // Published shipping policy fallback, checked 2026-10-15. Business days AFTER dispatch.
  function policyForCountry(country) {
    if (['FR','BE','CH','LU','MC','GB'].includes(country)) return 'policy_europe';
    if (['CA','US'].includes(country)) return 'policy_international';
    return null;
  }
  // Policy fallback verified 2026-10-30: 24h preparation, then 5–9 / 7–15 working days.
  // French national holidays: calendrier.api.gouv.fr/jours-feries/metropole.json.
  const frenchHolidayCache = new Map();
  function frenchHolidays(year) {
    if (frenchHolidayCache.has(year)) return frenchHolidayCache.get(year);
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = (h + l - 7 * m + 114) % 31 + 1;
    const easter = year + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0');
    const holidays = new Set(['01-01','05-01','05-08','07-14','08-15','11-01','11-11','12-25'].map(value => year + '-' + value));
    [1, 39, 50].forEach(offset => holidays.add(addCalendarDays(easter, offset)));
    frenchHolidayCache.set(year, holidays);
    return holidays;
  }
  function addWorkingDays(day, count, country) {
    const date = isoDate(day);
    if (!date || !Number.isInteger(count) || count < 0 || count > 365) return null;
    let remaining = count;
    while (remaining > 0) {
      date.setUTCDate(date.getUTCDate() + 1);
      const iso = date.toISOString().slice(0, 10);
      const holiday = country === 'FR' && frenchHolidays(date.getUTCFullYear()).has(iso);
      if (date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !holiday) remaining--;
    }
    return date.toISOString().slice(0, 10);
  }
  function policyEstimate({ country, now = new Date(), available = true, subscription = false }) {
    if (!available) return { status: 'unavailable' };
    const key = policyForCountry(country);
    if (!key || !Number.isFinite(now.valueOf())) return { status: 'unsupported' };
    const dispatchDay = calendarDate(new Date(now.valueOf() + DAY));
    const range = key === 'policy_europe' ? [5, 9] : [7, 15];
    return { status: 'ready', source: 'policy', start: addWorkingDays(dispatchDay, range[0], country), end: addWorkingDays(dispatchDay, range[1], country), label: 'Livraison entre le' };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calendarDate, addCalendarDays, windowForCountry, estimate, policyForCountry, frenchHolidays, addWorkingDays, policyEstimate };
  if (typeof document === 'undefined') return;

  // Dismiss delivery help without intercepting clicks on its text, trigger or links.
  const openDeliveryDetails = () => document.querySelectorAll('[data-native-delivery] .native-delivery__details[open]');
  document.addEventListener('click', event => {
    openDeliveryDetails().forEach(details => {
      if (!details.contains(event.target)) details.open = false;
    });
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const opened = openDeliveryDetails();
    if (!opened.length) return;
    opened.forEach(details => {
      const restoreFocus = details.contains(document.activeElement);
      details.open = false;
      if (restoreFocus) {
        details.querySelector('summary')?.focus({ preventScroll: true });
        event.preventDefault();
      }
    });
  });

  const initialized = new WeakSet();
  const sources = new Map();
  function load(url) {
    if (!sources.has(url)) sources.set(url, fetch(url, { credentials: 'same-origin' }).then(response => {
      if (!response.ok) throw new Error('Delivery data unavailable');
      return response.json();
    }));
    return sources.get(url);
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-native-delivery]').forEach(async widget => {
      if (initialized.has(widget)) return;
      initialized.add(widget);
      const homeEstimate = widget.hasAttribute('data-home-estimate');
      const root = homeEstimate ? widget : widget.closest('[data-product-root]');
      const selector = widget.querySelector('[data-delivery-country]');
      const message = widget.querySelector('[data-delivery-message]');
      const note = widget.querySelector('[data-delivery-note]');
      const help = widget.querySelector('[data-delivery-help]');
      const controls = widget.querySelector('[data-delivery-controls]');
      const flag = widget.querySelector('[data-delivery-flag]');
      if (!root || !selector || !message || !note || !controls) return;
      try {
        const savedCountry = deliveryCountryFromStorage(sessionStorage, document.documentElement.dataset.marketCountry, window.Shopify?.currency?.active);
        if (savedCountry && Array.from(selector.options).some(option => option.value === savedCountry)) selector.value = savedCountry;
      } catch (_) { /* Storage may be unavailable; keep the Shopify destination. */ }
      let variants = {};
      try { variants = JSON.parse(widget.querySelector('[data-delivery-variants]').textContent); } catch (_) { return; }
      let strings = {};
      try { strings = JSON.parse(widget.querySelector('[data-delivery-strings]')?.textContent || '{}'); } catch (_) { /* Preserve readable fallback copy. */ }
      const copy = (key, fallback) => typeof strings[key] === 'string' && strings[key] ? strings[key] : fallback;
      let data = null;
      let lastSignature = '';
      function update() {
        const variantId = root.querySelector('input[name="id"][data-variant-id]')?.value;
        const available = homeEstimate || variants[variantId] === true;
        widget.hidden = !available;
        if (!available) return;
        const plan = root.querySelector('[data-selling-plan]');
        const options = { data, country: selector.value, available, subscription: !!(plan && !plan.disabled && plan.value) };
        const observed = estimate(options);
        const result = !homeEstimate && observed.status !== 'ready' ? policyEstimate(options) : observed;
        // On the homepage, omit the question when there is no reliable answer.
        // Product pages retain their explicit fallback and assistance link.
        if (homeEstimate) {
          const faqItem = widget.closest('[data-delivery-faq]');
          if (faqItem) faqItem.hidden = result.status !== 'ready';
        }
        const policyKey = !homeEstimate && policyForCountry(selector.value);
        const signature = JSON.stringify({ result, country: selector.value });
        if (signature === lastSignature) return;
        lastSignature = signature;
        if (help) help.hidden = result.status === 'ready' || !!policyKey;
        message.textContent = '';
        if (result.status === 'ready') {
          const includeYear = result.start.slice(0, 4) !== result.end.slice(0, 4);
          const format = new Intl.DateTimeFormat(document.documentElement.lang || 'fr-FR', { timeZone: 'UTC', day: 'numeric', month: 'short', ...(includeYear ? { year: 'numeric' } : {}) });
          const heading = document.createElement('strong');
          heading.textContent = copy('estimated', result.label);
          message.append(heading, document.createTextNode(' '));
          const first = document.createElement('time'); first.dateTime = result.start; first.textContent = format.format(isoDate(result.start));
          first.setAttribute('aria-label', format.format(isoDate(result.start)));
          const last = document.createElement('time'); last.dateTime = result.end; last.textContent = format.format(isoDate(result.end));
          message.append(first, document.createTextNode(' ' + copy('between_separator', 'et le') + ' '), last);
          note.textContent = result.source === 'policy'
            ? copy(selector.value === 'FR' ? 'policy_dates_note_fr' : 'policy_dates_note', 'Dates estimated from our published delivery times, including preparation. Local holidays, transport or customs may affect delivery.')
            : copy('source_note', 'Estimation indicative d’après nos livraisons récentes, préparation incluse. La date peut varier.');
        } else if (policyKey) {
          message.textContent = copy(policyKey, copy('fallback', 'Délais selon destination.'));
          note.textContent = copy(selector.value === 'FR' ? 'policy_note_fr' : 'policy_note', copy('country_note', 'Les délais dépendent du pays de livraison.'));
        } else {
          message.textContent = result.status === 'unsupported' ? copy('unsupported', 'Estimation sur demande.') : copy('fallback', 'Délais selon destination.');
          note.textContent = copy('country_note', 'Les délais dépendent du pays de livraison.');
        }
      }
      function schedule() { requestAnimationFrame(update); }
      function publishCountry() {
        if (flag) {
          // Inline flags cover the countries with delivery evidence, including Windows without flag emoji support.
          const flags = {
            FR: '<path fill="#002395" d="M0 0h10v20H0z"/><path fill="#fff" d="M10 0h10v20H10z"/><path fill="#ed2939" d="M20 0h10v20H20z"/>',
            BE: '<path fill="#000" d="M0 0h10v20H0z"/><path fill="#fdda24" d="M10 0h10v20H10z"/><path fill="#ef3340" d="M20 0h10v20H20z"/>',
            CH: '<path fill="#da291c" d="M0 0h30v20H0z"/><path fill="#fff" d="M12 3h6v4h4v6h-4v4h-6v-4H8V7h4z"/>',
            CA: '<path fill="#fff" d="M0 0h30v20H0z"/><path fill="#d80621" d="M0 0h7v20H0zM23 0h7v20h-7zM15 3l2 4 2-1-1 4 3-1-1 4-4 2v3h-2v-3l-4-2-1-4 3 1-1-4 2 1z"/>',
            LU: '<path fill="#ed2939" d="M0 0h30v7H0z"/><path fill="#fff" d="M0 7h30v6H0z"/><path fill="#00a1de" d="M0 13h30v7H0z"/>',
            MC: '<path fill="#ce1126" d="M0 0h30v10H0z"/><path fill="#fff" d="M0 10h30v10H0z"/>',
            GB: '<path fill="#012169" d="M0 0h30v20H0z"/><path stroke="#fff" stroke-width="5" d="m0 0 30 20M30 0 0 20"/><path stroke="#c8102e" stroke-width="2" d="m0 0 30 20M30 0 0 20"/><path stroke="#fff" stroke-width="7" d="M15 0v20M0 10h30"/><path stroke="#c8102e" stroke-width="4" d="M15 0v20M0 10h30"/>',
            US: '<path fill="#fff" d="M0 0h30v20H0z"/><path stroke="#b31942" stroke-width="1.54" d="M0 .77h30M0 3.85h30M0 6.92h30M0 10h30M0 13.08h30M0 16.15h30M0 19.23h30"/><path fill="#0a3161" d="M0 0h13v10.77H0z"/><path stroke="#fff" stroke-width="1" stroke-linecap="round" stroke-dasharray=".01 2" d="M1 1.2h11M2 3.2h9M1 5.2h11M2 7.2h9M1 9.2h11"/>'
          };
          const code = selector.value;
          if (flags[code]) flag.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 20" focusable="false" aria-hidden="true">' + flags[code] + '</svg>';
          else flag.textContent = /^[A-Z]{2}$/.test(code) ? String.fromCodePoint(...Array.from(code, letter => 127397 + letter.charCodeAt(0))) : '';
        }
        root.dataset.deliveryCountry = selector.value;
        document.documentElement.dataset.deliveryCountry = selector.value;
        try { saveDeliveryCountry(sessionStorage, selector.value, document.documentElement.dataset.marketCountry, window.Shopify?.currency?.active); } catch (_) {}
        root.dispatchEvent(new CustomEvent('delivery:country:change', { bubbles: true, detail: { country: selector.value } }));
      }
      selector.addEventListener('change', publishCountry);
      publishCountry();
      root.addEventListener('change', schedule);
      const observer = new MutationObserver(schedule);
      observer.observe(root, { attributes: true, subtree: true, attributeFilter: ['data-purchase-type', 'value', 'disabled'] });
      controls.hidden = false;
      selector.disabled = false;
      update();
      try {
        data = await load(widget.dataset.source);
      } catch (_) { /* Keep the policy fallback when the static asset is unavailable. */ }
      update();
      document.addEventListener('visibilitychange', () => { if (!document.hidden && widget.isConnected) update(); });
      const refresh = setInterval(() => {
        if (!widget.isConnected) { clearInterval(refresh); observer.disconnect(); return; }
        if (!document.hidden) update();
      }, 60000);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

